import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  MenuItem,
  Alert,
  Autocomplete,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Chip,
  Stack,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import JobSupportSeekerWorkflowDrawer from './JobSupportSeekerWorkflowDrawer';

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Asia/Kolkata', label: 'India (IST)' },
  { value: 'UTC', label: 'UTC' },
];

const PAY_PERIODS = [
  { value: 'hour', label: 'Per hour' },
  { value: 'week', label: 'Per week' },
  { value: 'month', label: 'Per month' },
];

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return name ? `${name} (${c.email || c.contact_type || ''})` : c.email || c.id;
}

function providerLabel(p) {
  if (!p) return '—';
  const person = contactLabel(p.contact);
  if (p.provider_kind === 'company' && (p.company?.company_name || p.company?.name)) {
    return `${person} · ${p.company.company_name || p.company.name}`;
  }
  return person || p.provider_number || '—';
}

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

export default function HelpDeskJobSupportSeeker() {
  const { hasPermission, isAdmin } = useAuth();
  const isStaff = isAdmin || hasPermission('helpdesk_support') || hasPermission('helpdesk_modify');
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState([]);
  const [providers, setProviders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactQuery, setContactQuery] = useState('');
  const [selectedContact, setSelectedContact] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [termsText, setTermsText] = useState('');
  const [termsVersion, setTermsVersion] = useState('');

  const [form, setForm] = useState({
    expected_time: '',
    expected_start_date: '',
    expected_end_date: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles',
    hours_per_day: '',
    days_per_week: '',
    pay_amount: '',
    pay_period: 'month',
    job_responsibility: '',
    skills_needed: '',
    additional_notes: '',
  });

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await api.get('/helpdesk/job-support', { params: { scope: isStaff ? 'all' : 'my' } });
      setRows(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch {
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, [isStaff]);

  useEffect(() => {
    loadList();
    api.get('/helpdesk/job-support/providers', { params: { status: 'active' } })
      .then((r) => setProviders(Array.isArray(r.data?.data) ? r.data.data : []))
      .catch(() => setProviders([]));
    api.get('/helpdesk/job-support/workflow-meta')
      .then((r) => {
        setTermsText(r.data?.data?.payment_terms_text || '');
        setTermsVersion(r.data?.data?.payment_terms_version || '');
      })
      .catch(() => {});
  }, [loadList]);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .get('/helpdesk/job-support/eligible-contacts', { params: { q: contactQuery || undefined, limit: 100 } })
        .then((r) => {
          if (!cancelled) setContacts(Array.isArray(r.data?.data) ? r.data.data : []);
        })
        .catch(() => {
          if (!cancelled) setContacts([]);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [contactQuery]);

  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const openRow = (id) => {
    setDrawerId(id);
    setDrawerOpen(true);
  };

  const submitNew = async (e) => {
    e.preventDefault();
    setError('');
    if (!selectedContact?.id) {
      setError('Select a Job Support Seeker.');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post('/helpdesk/job-support', {
        contact_id: selectedContact.id,
        ...form,
        hours_per_day: form.hours_per_day || null,
        days_per_week: form.days_per_week || null,
        pay_amount: form.pay_amount || null,
        expected_start_date: form.expected_start_date || null,
        expected_end_date: form.expected_end_date || null,
      });
      const id = r.data?.data?.id;
      await loadList();
      setTab(0);
      if (id) openRow(id);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Job Support Seeker
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        View/List all seeker requests. Open a row to advance the 8-step workflow in the side panel (same pattern as
        Immigration → Process H1).
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
        <Tab label="View / List" />
        <Tab label="Add Job Support Seeker" />
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TabPanel value={tab} index={0}>
        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Request #</TableCell>
                    <TableCell>Seeker</TableCell>
                    <TableCell>Dates</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell>Assigned provider</TableCell>
                    <TableCell>Pay</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingList ? (
                    <TableRow>
                      <TableCell colSpan={7}>
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7}>No Job Support Seeker requests yet.</TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r) => (
                      <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => openRow(r.id)}>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.request_number}</TableCell>
                        <TableCell>{contactLabel(r.contact)}</TableCell>
                        <TableCell>
                          {(r.expected_start_date || '—') + ' → ' + (r.expected_end_date || '—')}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={`Step ${r.workflow_step || 1}`} />
                        </TableCell>
                        <TableCell>{providerLabel(r.provider)}</TableCell>
                        <TableCell>
                          {r.pay_amount != null ? `${r.pay_amount} / ${r.pay_period || '—'}` : '—'}
                        </TableCell>
                        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                          <Button size="small" onClick={() => openRow(r.id)}>
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      <TabPanel value={tab} index={1}>
        <Paper sx={{ p: 2, maxWidth: 900 }} component="form" onSubmit={submitNew}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
            Add Job Support Seeker
          </Typography>
          <Autocomplete
            options={contacts}
            getOptionLabel={contactLabel}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            value={selectedContact}
            onChange={(_, v) => setSelectedContact(v)}
            onInputChange={(_, v) => setContactQuery(v)}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params} label="Job Support Seeker" required margin="normal" />
            )}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected start date"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={form.expected_start_date}
                onChange={(e) => patch('expected_start_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected end date"
                type="date"
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
                value={form.expected_end_date}
                onChange={(e) => patch('expected_end_date', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Expected time / availability"
                fullWidth
                margin="normal"
                value={form.expected_time}
                onChange={(e) => patch('expected_time', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Timezone"
                fullWidth
                margin="normal"
                value={form.timezone}
                onChange={(e) => patch('timezone', e.target.value)}
              >
                {TIMEZONES.map((z) => (
                  <MenuItem key={z.value} value={z.value}>
                    {z.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Hours per day"
                type="number"
                fullWidth
                margin="normal"
                value={form.hours_per_day}
                onChange={(e) => patch('hours_per_day', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Days per week"
                type="number"
                fullWidth
                margin="normal"
                value={form.days_per_week}
                onChange={(e) => patch('days_per_week', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Pay period"
                fullWidth
                margin="normal"
                value={form.pay_period}
                onChange={(e) => patch('pay_period', e.target.value)}
              >
                {PAY_PERIODS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Interested to pay (amount)"
                type="number"
                fullWidth
                margin="normal"
                value={form.pay_amount}
                onChange={(e) => patch('pay_amount', e.target.value)}
              />
            </Grid>
          </Grid>
          <TextField
            label="Actual job responsibility"
            fullWidth
            multiline
            minRows={3}
            margin="normal"
            value={form.job_responsibility}
            onChange={(e) => patch('job_responsibility', e.target.value)}
          />
          <TextField
            label="Skills needed"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={form.skills_needed}
            onChange={(e) => patch('skills_needed', e.target.value)}
          />
          <TextField
            label="Additional notes"
            fullWidth
            multiline
            minRows={2}
            margin="normal"
            value={form.additional_notes}
            onChange={(e) => patch('additional_notes', e.target.value)}
          />
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Submitting…' : 'Submit request'}
            </Button>
            <Button onClick={() => setTab(0)}>Back to list</Button>
          </Stack>
        </Paper>
      </TabPanel>

      <JobSupportSeekerWorkflowDrawer
        requestId={drawerId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onUpdated={loadList}
        providers={providers}
        termsText={termsText}
        termsVersion={termsVersion}
      />
    </Box>
  );
}
